import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import { getValidSitecoreLink } from 'components/SitecoreLink';
import { useEffectOnce } from 'hooks';
import { useProductListGTMEvents } from 'hooks/gtm';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type {
	ColorField,
	Field,
	SitecoreBlockProps,
	SitecoreLink,
} from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { getAsset, is } from 'utils/helpers';

import PromoHero from './PromoHero';

interface Props extends SitecoreBlockProps {
	fields?: {
		campaignBubbleLabel?: Field<string>;
		campaignBubbleSale?: Field<boolean>;
		campaignBubbleText?: Field<string>;
		datasourceName?: string;
		largeImage?: Field<DigizuiteAsset[]>;
		preventAutoplay?: Field<boolean>;
		primaryLink?: Field<SitecoreLink>;
		products?: ProductCard[];
		secondaryLink?: Field<SitecoreLink>;
		smallImage?: Field<DigizuiteAsset[]>;
		tertiaryLink?: Field<SitecoreLink>;
		textBackgroundColor?: ColorField;
		video?: Field<DigizuiteAsset[]>;
	};
}

export default function PromoHeroBlock({ fields, params }: Props) {
	const { gtmItemListId, gtmItemListName, sendViewItemListEventOnce } =
		useProductListGTMEvents(
			'block',
			`Block: ${fields?.datasourceName ?? 'PromoHeroUnknown'}`,
		);
	useEffectOnce(() => {
		if (fields?.products?.length) {
			sendViewItemListEventOnce(fields.products, fields.products.length);
		}
	});

	if (!fields) {
		return null;
	}

	const {
		largeImage,
		preventAutoplay,
		primaryLink,
		products,
		secondaryLink,
		smallImage,
		tertiaryLink,
		video,
	} = fields;
	const smallImg = getAsset('Templated 4:3', smallImage);
	const largeImg = getAsset('Templated 4:1', largeImage);
	const videoAsset = getAsset('Source Copy', video);

	const links = [primaryLink, secondaryLink, tertiaryLink]
		.map((link) => getValidSitecoreLink(link?.value))
		.filter(is.truthy);

	if ((!smallImg || !largeImg) && !video) {
		return (
			<ComponentPlaceholder
				componentName="PromoHero"
				className={getEditorMargin(params)}
				description="This component requires a 'Small Image' and a 'Large Image' or a 'Video' asset."
			/>
		);
	}

	return (
		<LayoutContainer
			id={params?.anchor}
			gutters="md"
			outerClassName={getEditorMargin(params)}
		>
			{largeImg && smallImg && (
				<PromoHero
					basePriceTextBackground={
						fields.textBackgroundColor?.fields?.colorHex?.value
					}
					campaignBubbleLabel={fields.campaignBubbleLabel?.value}
					campaignBubbleSale={fields.campaignBubbleSale?.value}
					campaignBubbleText={fields.campaignBubbleText?.value}
					gtmItemListId={gtmItemListId}
					gtmItemListName={gtmItemListName}
					largeImageAlt={largeImg.alt}
					largeImageSrc={largeImg.src}
					links={links}
					preventAutoplay={preventAutoplay?.value}
					products={products}
					primaryLinkHref={
						primaryLink?.value.linktype === 'popover'
							? undefined
							: primaryLink?.value.href
					}
					smallImageAlt={smallImg.alt}
					smallImageSrc={smallImg.src}
					video={videoAsset}
				/>
			)}
		</LayoutContainer>
	);
}
PromoHeroBlock.displayName = 'PromoHeroBlock';
